import { computed, inject, Injectable, signal } from '@angular/core';
import { AuthAdminApiService } from '../services/auth-admin-api.service';
import { LoginAdminRequestDto, UserAdminToken, UserLoginData } from '../models/auth.models';
import { TokenStorageUtil } from '../utils/token-storage.util';
import { JwtUtil } from '@ribera-web/token-decodifier-service';
import { Router } from '@angular/router';
import { InactivitySessionUtil } from '../utils/inactivity-session.util';

@Injectable({
  providedIn: 'root'
})
export class AuthAdminStore {

  private api = inject(AuthAdminApiService);
  private tokenStorage = inject(TokenStorageUtil);
  private jwtUtil = inject(JwtUtil);
  private router = inject(Router);
  private inactivitySession = inject(InactivitySessionUtil);

  private _user = signal<UserLoginData | null>(null);
  readonly user = computed(() => this._user());

  private _error = signal<string | null>(null);
  readonly error = computed(() => this._error());

  private _userLoginData = signal<UserLoginData | null>(null);
  readonly userLoginData = computed(() => this._userLoginData());

  isAuthenticated = signal(false);

  constructor() {
    const token = this.tokenStorage.get();
    if (!token) return;

    const exp = this.jwtUtil.getExpirationDate(token);
    const expiresInMs = (exp * 1000) - Date.now();

    if (expiresInMs <= 0) {
      this.logout();
      return;
    }

    this.isAuthenticated.set(true);
    this.scheduleTokenRefresh(exp);
    this.inactivitySession.startWatching();
    this.getUserLoginData();
  }

  login(dto: LoginAdminRequestDto) {
    this.api.login(dto).subscribe({
      next: res => {
        this.tokenStorage.save(res.data?.access_token || '');
        this.scheduleTokenRefresh(this.jwtUtil.getExpirationDate(res.data?.access_token || ''));
        this.inactivitySession.startWatching();
        this.isAuthenticated.set(true);
        this._error.set(null);
        this.getUserLoginData();
      },
      error: err => {
        const backendMessage =
          err.error?.message ?? 'Error al iniciar sesión';

        this._error.set(backendMessage);
        this.isAuthenticated.set(false);
      }
    });
  }

  getUserLoginData() {
    this.api.getUserLoginData().subscribe({
      next: res => {
        if (res?.permissions) {
          this.setPermissions(this.normalizePermissions(res.permissions, res.rolename));
        } else {
          this.setPermissions(this.normalizePermissions('', res?.rolename));
        }
        this._userLoginData.set(res || null);
        this._user.set(res);
      }
    });
  }

  // async refreshAccessToken(): Promise<string | null> {
  //   try {
  //     const res = await firstValueFrom(this.api.refreshToken());
  //     this.tokenStorage.save(res.data?.access_token || '');
  //     return res.data?.access_token || null;
  //   } catch {
  //     this.logout();
  //     return null;
  //   }
  // }

  refreshAccessToken() {
    this.api.refreshToken().subscribe({
      next: res => {
        this.tokenStorage.save(res.data?.access_token || '');
        this.scheduleTokenRefresh(this.jwtUtil.getExpirationDate(res.data?.access_token || ''));
        this.inactivitySession.startWatching();
        this.isAuthenticated.set(true);
      },
      error: () => {
        this.logout();
        return null;
      }
    });
  }

  logout() {
    this.clearSession();
    this._user.set(null);
    this.isAuthenticated.set(false);
    this.inactivitySession.stopWatching();
    window.location.href = '/login';
  }

  clearSession() {
    this.isAuthenticated.set(false);
    this.tokenStorage.clear();

    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
    }
  }

  logoutTimer: any;

  scheduleTokenRefresh(exp: number) {
    const expiresInMs = (exp * 1000) - Date.now();

    if (expiresInMs <= 0) {
      this.logout();
      return;
    }

    this.logoutTimer = setTimeout(() => {

      const elapsedSinceLastActivity = Date.now() - this.inactivitySession.lastActivity();
      if (elapsedSinceLastActivity < this.inactivitySession.TIMEOUT) {
        this.refreshAccessToken();
      } else {
        this.logout();
      }
    }, expiresInMs - 60000); // Refresh 1 minute before expiration
  }

  private _permissions = signal<Set<string>>(new Set());

  public permissions = this._permissions.asReadonly();

  setPermissions(code: string[]) {
    this._permissions.set(new Set(code));
  }

  hasPermission(code: string): boolean {
    if (this._permissions().has('SUPER_ADMIN')) return true;

    return this._permissions().has(code);
  }

  private normalizePermissions(permissions: string, roleName?: string): string[] {
    const codes = permissions
      .split(',')
      .map(code => code.trim())
      .filter(Boolean);

    const normalizedRoleName = roleName?.trim().replace(/\s+/g, ' ').toUpperCase();

    if (normalizedRoleName === 'SUPER ADMIN' && !codes.includes('SUPER_ADMIN')) {
      codes.push('SUPER_ADMIN');
    }

    return codes.length ? codes : ['NO_PERMISSIONS'];
  }

}
